import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [leadDone, setLeadDone] = useState(() => !!sessionStorage.getItem('bg_chat_lead'));
  const [sessionId] = useState(() => {
    let id = sessionStorage.getItem('bg_chat_session');
    if (!id) {
      id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('bg_chat_session', id);
    }
    return id;
  });
  const [lead, setLead] = useState({ name: '', email: '', phone: '' });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hello! I\'m AquaBot, your water treatment assistant. Ask me about STP, ETP, RO plants, boilers, chemicals — or request a quote!' }
  ]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim() || !lead.phone.trim()) {
      setLeadError('Please fill in all fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) {
      setLeadError('Please enter a valid email address.');
      return;
    }
    setLeadLoading(true);
    setLeadError('');
    try {
      const res = await fetch(`${API}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, ...lead })
      });
      if (!res.ok) throw new Error('Failed');
      sessionStorage.setItem('bg_chat_lead', '1');
      setLeadDone(true);
      setMessages((m) => [...m.slice(0, 1), { role: 'assistant', text: `Thanks ${lead.name.split(' ')[0]}! How can I help you today? Looking for a water treatment plant, boiler or chemicals?` }]);
    } catch {
      setLeadError('Please enter a valid email and try again.');
    } finally {
      setLeadLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text })
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const data = JSON.parse(payload);
            const chunk = data.delta || data.error || '';
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', text: copy[copy.length - 1].text + chunk };
                return copy;
              });
            }
          } catch { /* skip malformed chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', text: 'Connection issue — please try again or call +91 98142 04405.' };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          data-testid="chat-widget-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-primary hover:bg-blue-700 text-white rounded-full shadow-2xl px-5 py-4 transition-transform hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="hidden sm:inline font-semibold text-sm">Get a Quote</span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-4 sm:right-6 z-50 w-[92vw] sm:w-[380px] h-[540px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-full p-1.5"><Bot className="w-5 h-5" /></div>
              <div>
                <p className="font-semibold text-sm leading-tight">AquaBot — BG Techno Chem</p>
                <p className="text-[11px] text-blue-100">AI assistant · Instant quotes & answers</p>
              </div>
            </div>
            <button data-testid="chat-widget-close-button" onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-full p-1.5" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!leadDone ? (
            /* Lead Capture Form */
            <form onSubmit={submitLead} className="flex-1 flex flex-col justify-center gap-3 px-5 py-6 bg-slate-50" data-testid="chat-lead-form">
              <p className="text-slate-800 font-semibold text-sm">Let's get started 👋</p>
              <p className="text-slate-500 text-xs mb-1">Share your details so our team can send you quotes & follow up.</p>
              <Input data-testid="chat-lead-name-input" placeholder="Your Name" value={lead.name} onChange={(e) => { setLeadError(''); setLead({ ...lead, name: e.target.value }); }} />
              <Input data-testid="chat-lead-email-input" type="email" placeholder="Email Address" value={lead.email} onChange={(e) => { setLeadError(''); setLead({ ...lead, email: e.target.value }); }} />
              <Input data-testid="chat-lead-phone-input" type="tel" placeholder="Phone Number" value={lead.phone} onChange={(e) => { setLeadError(''); setLead({ ...lead, phone: e.target.value }); }} />
              {leadError && <p data-testid="chat-lead-error" className="text-red-500 text-xs">{leadError}</p>}
              <Button data-testid="chat-lead-submit-button" type="submit" disabled={leadLoading} className="bg-primary hover:bg-blue-700 mt-1">
                {leadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Chat'}
              </Button>
              <p className="text-[10px] text-slate-400 text-center">We respect your privacy. Details used only to assist you.</p>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50" data-testid="chat-messages-container">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      data-testid={m.role === 'user' ? 'chat-user-message' : 'chat-bot-message'}
                      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === 'user' ? 'bg-primary text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
                      }`}
                    >
                      {m.text || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : '')}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="border-t border-slate-200 p-3 flex gap-2 bg-white">
                <Input
                  data-testid="chat-message-input"
                  placeholder="Ask about plants, chemicals, quotes..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  disabled={streaming}
                />
                <Button data-testid="chat-send-button" onClick={sendMessage} disabled={streaming || !input.trim()} size="icon" className="bg-primary hover:bg-blue-700 flex-shrink-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
